import React, { useState, useEffect } from 'react';
import {
  FileCheck,
  CheckCircle2,
  CheckCircle,
  RefreshCw,
  Save,
  Edit3,
  Upload,
  ZoomIn,
  X,
  ExternalLink,
  BookOpen,
  AlertCircle
} from 'lucide-react';
import confetti from 'canvas-confetti';

interface RecentJournalEntriesTableProps {
  onOpenFullLedger?: () => void;
  className?: string;
}

export const RecentJournalEntriesTable: React.FC<RecentJournalEntriesTableProps> = ({
  onOpenFullLedger,
  className = ''
}) => {
  const [ledgerEntries, setLedgerEntries] = useState<any[]>([]);
  const [slipsMap, setSlipsMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Editing & Uploading state
  const [editingJournalId, setEditingJournalId] = useState<string | null>(null);
  const [tungNgernInputs, setTungNgernInputs] = useState<Record<string, string>>({});
  const [savingTungNgernId, setSavingTungNgernId] = useState<string | null>(null);
  const [uploadingSlipJournalId, setUploadingSlipJournalId] = useState<string | null>(null);
  const [selectedSlipModal, setSelectedSlipModal] = useState<any | null>(null);

  // Fetch Ledger and KTB Slips
  const fetchSlips = async () => {
    try {
      const res = await fetch('/api/payment/slip/ktb');
      if (res.ok) {
        const data = await res.json();
        const map: Record<string, any> = {};
        (data.slips || []).forEach((s: any) => {
          if (s.journalId) map[s.journalId] = s;
          if (s.referenceCode) map[s.referenceCode] = s;
        });
        setSlipsMap(map);
      }
    } catch (err) {
      console.error('Failed fetching KTB slips:', err);
    }
  };

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/payment/ledger');
      if (res.ok) {
        const data = await res.json();
        setLedgerEntries(data.ledger?.recentEntries || []);
      }
      await fetchSlips();
    } catch (err) {
      console.error('Failed fetching ledger:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();

    // Listen to cross-system order/payment updates
    const handleOrderUpdate = () => {
      fetchLedger();
    };
    window.addEventListener('order-updated', handleOrderUpdate);
    return () => {
      window.removeEventListener('order-updated', handleOrderUpdate);
    };
  }, []);

  // Handle Slip Upload with AI OCR
  const handleUploadSlip = async (journalId: string, file: File) => {
    try {
      setUploadingSlipJournalId(journalId);
      setErrorMsg('');
      setSuccessMsg('');

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result as string;
          const res = await fetch('/api/payment/slip/upload-ktb-slip', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image: base64,
              journalId,
              mimeType: file.type || 'image/jpeg',
              uploadedBy: 'Admin'
            })
          });

          const data = await res.json();
          if (!res.ok || !data.success) {
            throw new Error(data.error || 'การสกัดและตรวจสอบสลิปไม่สำเร็จ');
          }

          // Auto-populate extracted reference code in the input state
          setTungNgernInputs(prev => ({ ...prev, [journalId]: data.slipRecord.referenceCode }));
          
          setSuccessMsg(data.message || `สกัดรหัสอ้างอิงถุงเงิน "${data.slipRecord.referenceCode}" สำเร็จ! ยืนยันคำสั่งซื้อเป็น "รอดำเนินการ" และบันทึกในสตอเรจ KTB_SLIP เรียบร้อย`);
          
          await fetchLedger();

          // Dispatch event across the app for instant multi-device reactivity
          window.dispatchEvent(new CustomEvent('order-updated', { 
            detail: { orderId: data.orderId, status: 'Processing' } 
          }));

          confetti({
            particleCount: 60,
            spread: 70,
            origin: { y: 0.6 }
          });
        } catch (err: any) {
          setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการวิเคราะห์สลิป');
        } finally {
          setUploadingSlipJournalId(null);
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการอ่านไฟล์');
      setUploadingSlipJournalId(null);
    }
  };

  // Handle Manual Save of Tung Ngern Ref
  const handleSaveTungNgernRef = async (journalId: string) => {
    const refCode = (tungNgernInputs[journalId] ?? '').trim();
    if (!refCode) {
      setErrorMsg('กรุณากรอกรหัสอ้างอิงจากแอปถุงเงิน');
      return;
    }

    try {
      setSavingTungNgernId(journalId);
      setErrorMsg('');
      setSuccessMsg('');

      const res = await fetch('/api/payment/ledger/tungngern-ref', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journalId,
          tungNgernRef: refCode
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'ไม่สามารถบันทึกรหัสอ้างอิงได้');
      }

      setSuccessMsg(data.message || 'บันทึกรหัสอ้างอิงถุงเงิน และปรับสถานะออเดอร์เป็น "รอดำเนินการ" เรียบร้อยแล้ว');
      setEditingJournalId(null);
      await fetchLedger();

      window.dispatchEvent(new CustomEvent('order-updated', {
        detail: { orderId: data.orderId, status: 'Processing' }
      }));

      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 }
      });
    } catch (err: any) {
      setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการบันทึกรหัสอ้างอิง');
    } finally {
      setSavingTungNgernId(null);
    }
  };

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-xs p-4 sm:p-6 space-y-4 ${className}`}>
      {/* Alert Messages */}
      {successMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-xl flex items-center justify-between animate-in fade-in">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-semibold">{successMsg}</span>
          </div>
          <button onClick={() => setSuccessMsg('')} className="text-emerald-500 hover:text-emerald-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {errorMsg && (
        <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center justify-between animate-in fade-in">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span className="font-semibold">{errorMsg}</span>
          </div>
          <button onClick={() => setErrorMsg('')} className="text-rose-500 hover:text-rose-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div>
          <h4 className="text-xs sm:text-sm font-black text-slate-900 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-teal-600" />
            <span>รายการลงบัญชีรายวันล่าสุด (Recent Journal Entries)</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-800 border border-teal-200">
              KTB ถุงเงิน Sync & AI Slip OCR
            </span>
          </h4>
          <p className="text-[11px] text-slate-500 mt-0.5">
            สกัดสลิปด้วย AI เพื่อดึงรหัสอ้างอิง KTB หรือระบุเลข Ref เพื่อยืนยันคำสั่งซื้อเป็น "รอดำเนินการ" ทันที
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={fetchLedger}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-bold border border-slate-300 hover:border-slate-400 rounded-xl text-xs transition-all shadow-2xs cursor-pointer disabled:opacity-50"
            title="รีเฟรชข้อมูลรายการลงบัญชี"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-teal-600' : 'text-slate-500'}`} />
            <span>รีเฟรช</span>
          </button>

          {onOpenFullLedger && (
            <button
              type="button"
              onClick={onOpenFullLedger}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-800 font-bold border border-teal-200 rounded-xl text-xs transition-all shadow-2xs cursor-pointer"
              title="เปิดดูผังบัญชีแยกประเภทคู่ฉบับเต็ม"
            >
              <span>ผังบัญชีฉบับเต็ม</span>
              <ExternalLink className="w-3.5 h-3.5 text-teal-600" />
            </button>
          )}
        </div>
      </div>
      
      {/* Table Section */}
      <div className="border border-slate-200 rounded-xl overflow-x-auto shadow-2xs">
        <table className="w-full text-left text-xs min-w-[940px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
            <tr>
              <th className="p-3 font-mono">Journal ID</th>
              <th className="p-3">วันที่-เวลา</th>
              <th className="p-3">คำอธิบายรายการ</th>
              <th className="p-3">บัญชีเดบิต (Debit)</th>
              <th className="p-3">บัญชีเครดิต (Credit)</th>
              <th className="p-3 text-right">จำนวนเงิน (฿)</th>
              <th className="p-3 min-w-[190px] bg-amber-50/80 text-amber-900 border-l border-amber-200 font-black">
                <div className="flex items-center gap-1.5">
                  <FileCheck className="w-3.5 h-3.5 text-amber-600" />
                  <span>SLIP_PAYMENT</span>
                </div>
              </th>
              <th className="p-3 min-w-[210px] bg-teal-50/70 text-teal-900 border-l border-teal-100">
                รหัสอ้างอิงถุงเงิน
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {ledgerEntries.length > 0 ? (
              ledgerEntries.map((entry: any) => {
                const matchedSlip = slipsMap[entry.id] || (entry.tungNgernRef && slipsMap[entry.tungNgernRef]);
                return (
                  <tr key={entry.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-3 font-mono text-teal-700 font-bold whitespace-nowrap">{entry.id}</td>
                    <td className="p-3 text-slate-500 whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString('th-TH')}
                    </td>
                    <td className="p-3 font-medium text-slate-900">{entry.description}</td>
                    <td className="p-3 font-mono text-emerald-700 font-semibold">{entry.debitAccountId}</td>
                    <td className="p-3 font-mono text-indigo-700 font-semibold">{entry.creditAccountId}</td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                      ฿{Number(entry.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </td>
                    
                    {/* COLUMN: SLIP_PAYMENT */}
                    <td className="p-3 border-l border-amber-100 bg-amber-50/30">
                      {matchedSlip ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedSlipModal(matchedSlip)}
                            className="group relative w-10 h-10 rounded-lg border border-teal-300 overflow-hidden bg-slate-100 shadow-2xs hover:ring-2 hover:ring-teal-500 transition-all shrink-0 cursor-pointer"
                            title="คลิกเพื่อดูสลิป KTB_SLIP แบบเต็มและผลการตรวจสอบความถูกต้อง"
                          >
                            <img
                              src={matchedSlip.slipImageUrl}
                              alt="KTB Slip"
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                              onError={(e: any) => {
                                e.currentTarget.src = 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=100&auto=format&fit=crop&q=60';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ZoomIn className="w-3.5 h-3.5 text-white" />
                            </div>
                          </button>
                          
                          <div className="flex flex-col text-left">
                            <div className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                              <span>KTB Verified</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedSlipModal(matchedSlip)}
                              className="text-[10px] font-mono text-teal-700 hover:text-teal-900 underline text-left truncate max-w-[100px] cursor-pointer"
                              title="ดูสลิปในสตอเรจ KTB_SLIP"
                            >
                              Ref: {matchedSlip.referenceCode}
                            </button>
                          </div>

                          <label 
                            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded cursor-pointer transition-colors shrink-0"
                            title="อัปโหลดสลิปใหม่ทับ"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadSlip(entry.id, file);
                              }}
                            />
                          </label>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-teal-400 bg-teal-50 hover:bg-teal-100/90 text-teal-900 text-xs font-bold cursor-pointer transition-all shadow-2xs ${
                            uploadingSlipJournalId === entry.id ? 'opacity-60 pointer-events-none' : ''
                          }`}>
                            {uploadingSlipJournalId === entry.id ? (
                              <>
                                <RefreshCw className="w-3.5 h-3.5 animate-spin text-teal-700" />
                                <span className="text-[11px]">AI สกัดสลิป KTB...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-3.5 h-3.5 text-teal-700 shrink-0" />
                                <span className="text-[11px] whitespace-nowrap">อัปโหลดสลิปถุงเงิน</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadSlip(entry.id, file);
                              }}
                            />
                          </label>
                        </div>
                      )}
                    </td>

                    {/* COLUMN: รหัสอ้างอิงถุงเงิน */}
                    <td className="p-3 border-l border-slate-100 bg-slate-50/30">
                      {entry.tungNgernRef && editingJournalId !== entry.id ? (
                        <div className="flex items-center gap-2">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-900 font-mono font-bold text-xs shadow-2xs">
                            <CheckCircle className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                            <span>{entry.tungNgernRef}</span>
                          </div>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 whitespace-nowrap">
                            รอดำเนินการ
                          </span>
                          <button
                            onClick={() => {
                              setEditingJournalId(entry.id);
                              setTungNgernInputs(prev => ({ ...prev, [entry.id]: entry.tungNgernRef || '' }));
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded transition-colors"
                            title="แก้ไขรหัสอ้างอิง"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            placeholder="ระบุเลข Ref ธนาคาร..."
                            value={tungNgernInputs[entry.id] ?? (entry.tungNgernRef || '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              setTungNgernInputs(prev => ({ ...prev, [entry.id]: val }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveTungNgernRef(entry.id);
                              }
                            }}
                            className="w-36 sm:w-40 px-2.5 py-1 text-xs font-mono rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                          />
                          <button
                            onClick={() => handleSaveTungNgernRef(entry.id)}
                            disabled={savingTungNgernId === entry.id}
                            className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-lg shadow-2xs transition-all shrink-0 cursor-pointer"
                            title="บันทึกและยืนยันคำสั่งซื้อเป็น รอดำเนินการ"
                          >
                            {savingTungNgernId === entry.id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Save className="w-3 h-3 mr-1" />
                                <span>บันทึก</span>
                              </>
                            )}
                          </button>
                          {editingJournalId === entry.id && (
                            <button
                              onClick={() => setEditingJournalId(null)}
                              className="p-1 text-slate-400 hover:text-slate-600 text-[11px]"
                            >
                              ยกเลิก
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="p-8 text-center text-slate-400 font-medium">
                  {loading ? 'กำลังโหลดรายการลงบัญชี...' : 'ยังไม่มีรายการลงบัญชี (กดปุ่มจำลองชำระเงิน หรือเมื่อมีลูกค้าชำระเงินเพื่อสร้างรายการ)'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ========================================================= */}
      {/* MODAL: KTB TUNG-NGERN SLIP VIEWER & VERIFICATION DETAILS */}
      {/* ========================================================= */}
      {selectedSlipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200 animate-in zoom-in-95 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center shadow-2xs font-bold text-xs">
                  KTB
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                    <span>รายละเอียดสลิป KTB_SLIP</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      {selectedSlipModal.verificationStatus || 'VERIFIED'}
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-500 font-mono">
                    Primary Key (รหัสอ้างอิง): {selectedSlipModal.referenceCode}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedSlipModal(null)}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 overflow-y-auto space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Left: Slip Image */}
                <div className="flex flex-col items-center justify-center p-3 bg-slate-100/70 border border-slate-200 rounded-xl">
                  <img
                    src={selectedSlipModal.slipImageUrl}
                    alt="KTB Slip High Res"
                    className="max-h-72 w-auto object-contain rounded-lg shadow-sm border border-slate-200"
                    onError={(e: any) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="mt-2 text-center text-[10px] text-slate-400 font-mono break-all">
                    Storage: public/storage/KTB_SLIP/
                  </div>
                </div>

                {/* Right: Extracted Data & Validations */}
                <div className="space-y-3.5 text-xs">
                  <div className="p-3 bg-teal-50/70 rounded-xl border border-teal-200 space-y-1.5">
                    <div className="text-[10px] font-bold text-teal-800 uppercase tracking-wider">
                      รหัสอ้างอิงถุงเงิน (Primary Key)
                    </div>
                    <div className="text-base font-black font-mono text-teal-950">
                      {selectedSlipModal.referenceCode}
                    </div>
                  </div>

                  <div className="space-y-2 border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">หมายเลขคำสั่งซื้อ:</span>
                      <span className="font-bold text-slate-900 font-mono">{selectedSlipModal.orderId || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">รหัสธุรกรรม / Journal ID:</span>
                      <span className="font-bold text-teal-700 font-mono text-[11px]">{selectedSlipModal.transactionId || selectedSlipModal.journalId || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ยอดเงินที่ชำระ:</span>
                      <span className="font-black text-emerald-700 font-mono text-sm">฿{Number(selectedSlipModal.amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ชื่อร้านค้า (Merchant):</span>
                      <span className="font-bold text-slate-900">{selectedSlipModal.merchantName || 'GIFFFORYOU.COM'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">รหัสสมาชิก (MID):</span>
                      <span className="font-bold text-indigo-700 font-mono">108037654</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ผู้ชำระเงิน:</span>
                      <span className="font-medium text-slate-800">{selectedSlipModal.payerName || '-'} ({selectedSlipModal.payerAccountMasked || ''})</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ช่องทางชำระเงิน:</span>
                      <span className="font-medium text-slate-800">{selectedSlipModal.paymentChannel || 'QR รับเงิน'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-500">เวลาในสลิป:</span>
                      <span className="font-medium text-slate-700 text-[11px]">{selectedSlipModal.slipDate || '-'}</span>
                    </div>
                  </div>

                  {/* Verification Notes */}
                  {selectedSlipModal.verificationDetails?.validationNotes?.length > 0 && (
                    <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 space-y-1">
                      <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-emerald-600" />
                        <span>ผลการตรวจสอบสถาปัตยกรรม (Verification Report)</span>
                      </div>
                      <ul className="text-[11px] text-emerald-900 space-y-0.5 list-disc list-inside">
                        {selectedSlipModal.verificationDetails.validationNotes.map((note: string, idx: number) => (
                          <li key={idx}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="text-[10px] text-slate-400 font-mono truncate max-w-xs">
                SHA256: {selectedSlipModal.slipSha256 ? `${selectedSlipModal.slipSha256.substring(0, 16)}...` : '-'}
              </div>
              <button
                onClick={() => setSelectedSlipModal(null)}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
